import type {
    WorkOrderChangePriceData,
    AddRatioToActKc2StrategyEnum,
    WorkOrderChangeOtherPriceData,
} from 'gql/graphql';
import type { AnyObject } from 'types/AnyObject';
import { Button, ContextMenu, IconKebab } from 'uikit';

import * as R from 'ramda';
import { useCallback, useRef, useState } from 'react';

import { useWorkOrderChangeRowMenuItems } from './useWorkOrderChangeRowMenuItems';

const getItemLabel = R.prop('label');
const getItemOnClick = R.prop('action');
const getItemSubMenu = R.prop('menu');

type Item = {
    label: string;
    action: () => void;
    active?: boolean;
    menu: Item[];
};

interface IWorkOrderChangeRowMenu {
    rowData: WorkOrderChangePriceData;
    openSingleDeleteModalHandler?: () => void;
    checkSingleRowHandler?: ({ id, data }: { id: number; data: AnyObject | null }) => void;
    openAddMtrModalHandler?: () => void;
    openAddRatioModalHandler?: (strategy: AddRatioToActKc2StrategyEnum | null) => void;
    openEditOtherDistributionHandler?: () => void;
    openEditOtherModalHandler?: () => void;
    openViewOtherModalHandler?: () => void;
    openEditRatioModal?: () => void;
    openEditMtrModalHandler?: () => void;
    openEditNoteModalHandler?: () => void;
    setRowData?: (data: WorkOrderChangePriceData | (WorkOrderChangeOtherPriceData & { type: string })) => void;
    openEditUerModal?: () => void;
    isDisabled: boolean;
    openMoveVolumesBetweenSeasonsForUer?: () => void;
    isOpenMoveVolumesBetweenSeasonsForUerDisabled?: boolean;
    openMatchMtrDialogHandler?: () => void;
}

export const WorkOrderChangeRowMenu = (props: IWorkOrderChangeRowMenu) => {
    const {
        rowData,
        openSingleDeleteModalHandler,
        checkSingleRowHandler,
        openAddMtrModalHandler,
        openAddRatioModalHandler,
        isDisabled,
        openEditOtherDistributionHandler,
        openEditOtherModalHandler,
        openViewOtherModalHandler,
        openEditRatioModal,
        openEditMtrModalHandler,
        openEditNoteModalHandler,
        setRowData,
        openEditUerModal,
        openMoveVolumesBetweenSeasonsForUer,
        isOpenMoveVolumesBetweenSeasonsForUerDisabled,
        openMatchMtrDialogHandler,
    } = props;

    const [isOpen, setIsOpen] = useState<boolean>(false);
    const ref = useRef(null);

    const handleOnClick = useCallback(() => {
        setIsOpen((prev) => !prev);
    }, []);

    const { items, groups }: { items: Item[]; groups: { id: number }[] } = useWorkOrderChangeRowMenuItems({
        rowData,
        openSingleDeleteModalHandler,
        checkSingleRowHandler,
        openAddMtrModalHandler,
        openAddRatioModalHandler,
        openEditOtherDistributionHandler,
        openEditOtherModalHandler,
        openViewOtherModalHandler,
        openEditRatioModal,
        openEditMtrModalHandler,
        openEditNoteModalHandler,
        setRowData,
        openEditUerModal,
        openMoveVolumesBetweenSeasonsForUer,
        isOpenMoveVolumesBetweenSeasonsForUerDisabled,
        openMatchMtrDialogHandler,
    });

    const handleItemOnClick = useCallback(
        (item) => () => {
            const action = getItemOnClick(item) as () => void;

            action();
            setIsOpen(false);
        },
        [],
    );

    return (
        <>
            <Button
                disabled={isDisabled}
                iconLeft={IconKebab}
                onlyIcon
                view="clear"
                size="xs"
                style={{ marginTop: 12, marginBottom: 12, marginLeft: 4 }}
                onClick={handleOnClick}
                ref={ref}
            />

            <ContextMenu
                isOpen={isOpen}
                items={items}
                groups={groups}
                getItemLabel={getItemLabel}
                anchorRef={ref}
                direction="downStartLeft"
                getItemOnClick={handleItemOnClick}
                onClickOutside={handleOnClick}
                getItemSubMenu={getItemSubMenu}
            />
        </>
    );
};

WorkOrderChangeRowMenu.displayName = 'WorkOrderChangeRowMenu';
